/**
 * HTTPリクエストのエラーのStateを扱うモジュール
 */
import { ActionsUnion, createAction } from 'common/utils/action-utils';

export enum ActionTypes {
  FETCH_FAILURE = 'app/errors/FETCH_FAILURE',
  SPECIFIED_INVALID_URL = 'app/errors/SPECIFIED_INVALID_URL',
  PERFORMED_WRONG_OPERATION = 'app/errors/PERFORMED_WRONG_OPERATION',
  ALREADY_CONTRACTED = 'app/errors/ALREADY_CONTRACTED',
  PARTIALLY_DUPLICATED = 'app/errors/PARTIALLY_DUPLICATED',
  PURCHASES_ADDITIONAL = 'app/errors/PURCHASES_ADDITIONAL',
  DUPLICATE_PURCHASE = 'app/errors/DUPLICATE_PURCHASE',
  DUPLICATE_PURCHASED = 'app/errors/DUPLICATE_PURCHASED',
  PURCHASE_ERROR = 'app/errors/PURCHASE_ERROR',
  CANCEL_PURCHASE_ERROR = 'app/errors/CANCEL_PURCHASE_ERROR',
  CANCEL_CONFIRMATION_ERROR = 'app/errors/CANCEL_CONFIRMATION_ERROR',
  NOT_FOUND_PURCHASE_PLAN = 'app/errors/NOT_FOUND_PURCHASE_PLAN',
  SYSTEM_ERROR_OCURRED = 'app/errors/SYSTEM_ERROR_OCURRED',
  CLEAR = 'app/errors/CLEAR'
}

/**
 * fetch APIの失敗のActionを作成する
 * @param {ErrorState} payload エラー情報
 */
export const fetchFailure = (payload: ErrorResponse) =>
  createAction(ActionTypes.FETCH_FAILURE, payload);

/**
 * 不正なURLが指定された場合のActionを作成する
 */
export const specifiedInvalidUrl = (payload: ErrorResponse = {}) =>
  createAction(ActionTypes.SPECIFIED_INVALID_URL, payload);

/**
 * 無効な操作が行われた場合のActionを作成する
 */
export const performedWrongOperation = (payload: ErrorResponse = {}) =>
  createAction(ActionTypes.PERFORMED_WRONG_OPERATION, payload);

/**
 * 購入済みプランを購入しようとした場合のActionを作成する
 */
export const alreadyContracted = (payload: ErrorResponse = {}) =>
  createAction(ActionTypes.ALREADY_CONTRACTED, payload);

/**
 * 一部重複があった場合のActionを作成する
 */
export const partiallyDuplicated = (payload: ErrorResponse = {}) =>
  createAction(ActionTypes.PARTIALLY_DUPLICATED, payload);

/**
 * 購入手続きエラーがあった場合のActionを作成する
 */
export const purchaseError = (payload: ErrorResponse = {}) =>
  createAction(ActionTypes.PURCHASE_ERROR, payload);

/**
 * 利用制限のあるプランを追加購入した場合のActionを作成する
 */
export const purchasesAdditional = (payload: ErrorResponse = {}) =>
  createAction(ActionTypes.PURCHASES_ADDITIONAL, payload);

/**
 * 重複購入可能なプランを重複購入した場合のActionを作成する
 */
export const duplicatePurchase = (payload: ErrorResponse = {}) =>
  createAction(ActionTypes.DUPLICATE_PURCHASE, payload);

/**
 * 重複購入可能なプランで重複購入不可能だった場合のActionを作成する
 */
export const duplicatePurchased = (payload: ErrorResponse = {}) =>
  createAction(ActionTypes.DUPLICATE_PURCHASED, payload);

/**
 * 解約手続きエラーがあった場合のActionを作成する
 */
export const cancelPurchaseError = (payload: ErrorResponse = {}) =>
  createAction(ActionTypes.CANCEL_PURCHASE_ERROR, payload);

/**
 * 利用解除手続きエラーがあった場合のActionを作成する
 */
export const cancelConfirmationError = (payload: ErrorResponse = {}) =>
  createAction(ActionTypes.CANCEL_CONFIRMATION_ERROR, payload);

/**
 * 購入可能なプランがなかった場合のActionを作成する
 */
export const notFoundPurchasePlan = (payload: ErrorResponse = {}) =>
  createAction(ActionTypes.NOT_FOUND_PURCHASE_PLAN, payload);

/**
 * システムエラーが発生した場合のActionを作成する
 */
export const systemErrorOcurred = (payload: ErrorResponse = {}) =>
  createAction(ActionTypes.SYSTEM_ERROR_OCURRED, payload);

/**
 * ErrorStateをクリアする場合のActionを作成する
 */
export const clearErrors = () => createAction(ActionTypes.CLEAR);

const actions = {
  fetchFailure,
  specifiedInvalidUrl,
  performedWrongOperation,
  alreadyContracted,
  partiallyDuplicated,
  purchasesAdditional,
  duplicatePurchase,
  duplicatePurchased,
  purchaseError,
  cancelPurchaseError,
  cancelConfirmationError,
  notFoundPurchasePlan,
  systemErrorOcurred,
  clearErrors
};
type ErrorAction = ActionsUnion<typeof actions>;

export interface ErrorResponse {
  errors?: {
    code: string;
    message: string;
    field?: string;
    reason?: string;
  }[];
  requestId?: string;
  sasClientRequestId?: string;
  statusCode?: number;
}

export enum ErrorType {
  /** エラーレスポンス */
  ERROR_RESPONSE,
  /** 不正なURL */
  INVALID_URL,
  /** 購入済みのプラン */
  ALREADY_CONTRACTED,
  /** 一部重複 */
  PARTIALLY_DUPLICATED,
  /** 利用制限プランの追加購入 */
  PURCHASES_ADDITIONAL,
  /** 重複購入可能なプランの追加購入 */
  DUPLICATE_PURCHASE,
  /** 重複購入可能なプランの追加購入不可 */
  DUPLICATE_PURCHASED,
  /** 購入手続きエラー */
  PURCHASE_ERROR,
  /** 解約手続きエラー */
  CANCEL_PURCHASE_ERROR,
  /** 利用解除手続きエラー */
  CANCEL_CONFIRMATION_ERROR,
  /** 購入可能なプランがない */
  NOT_FOUND_PURCHASE_PLAN,
  /** 不正な操作 */
  WRONG_OPERATION,
  /** システムエラー、致命的なエラー */
  SYSTEM_ERROR
}

/** エラーのState */
export interface ErrorState {
  type?: ErrorType;
  /** エラーメッセージ */
  message?: string;
  /** エラーの詳細 */
  description?: string;
  /** ステータスコード */
  statusCode?: number;
  /** リクエストID */
  requestId?: string;
  /** SAS クライアントリクエストID */
  sasClientRequestId?: string;
}

const initialState: ErrorState = {};

export default (
  state: ErrorState = initialState,
  action: ErrorAction
): ErrorState => {
  // Fetch結果を、ErrorStateのObjectに変換。
  // エラーの詳細情報は先頭の結果を使用する
  const payloadToState = (payload: ErrorResponse): ErrorState => ({
    statusCode: payload.statusCode,
    message:
      payload.errors && payload.errors.length
        ? payload.errors[0].code
        : undefined,
    description:
      payload.errors && payload.errors.length
        ? payload.errors[0].message
        : undefined
  });

  switch (action.type) {
    case ActionTypes.FETCH_FAILURE:
      return {
        type: ErrorType.ERROR_RESPONSE,
        ...payloadToState(action.payload),
        // FETCH_FAILUREの時だけリクエストIDを更新する
        requestId: action.payload.requestId,
        sasClientRequestId: action.payload.sasClientRequestId
      };
    case ActionTypes.SPECIFIED_INVALID_URL:
      // statusCode=0が発生している時は、画面のUnloadよってfetchがAbortされた状態なので
      // URL不正エラーは無視する（各ActionCreatorの戻り値を修正するのは影響が大きいのでErrorのStateで対応）
      if (state.statusCode === 0) {
        return state;
      }
      return {
        ...state,
        type: ErrorType.INVALID_URL,
        ...payloadToState(action.payload)
      };
    case ActionTypes.PERFORMED_WRONG_OPERATION:
      return {
        ...state,
        type: ErrorType.WRONG_OPERATION,
        ...payloadToState(action.payload)
      };
    case ActionTypes.ALREADY_CONTRACTED:
      return {
        ...state,
        type: ErrorType.ALREADY_CONTRACTED,
        ...payloadToState(action.payload)
      };
    case ActionTypes.PARTIALLY_DUPLICATED:
      return {
        ...state,
        type: ErrorType.PARTIALLY_DUPLICATED,
        ...payloadToState(action.payload)
      };
    case ActionTypes.PURCHASES_ADDITIONAL:
      return {
        ...state,
        type: ErrorType.PURCHASES_ADDITIONAL,
        ...payloadToState(action.payload)
      };
    case ActionTypes.DUPLICATE_PURCHASE:
      return {
        ...state,
        type: ErrorType.DUPLICATE_PURCHASE,
        ...payloadToState(action.payload)
      };
    case ActionTypes.DUPLICATE_PURCHASED:
      return {
        ...state,
        type: ErrorType.DUPLICATE_PURCHASED,
        ...payloadToState(action.payload)
      };
    case ActionTypes.PURCHASE_ERROR:
      return {
        ...state,
        type: ErrorType.PURCHASE_ERROR,
        ...payloadToState(action.payload)
      };
    case ActionTypes.CANCEL_PURCHASE_ERROR:
      return {
        ...state,
        type: ErrorType.CANCEL_PURCHASE_ERROR,
        ...payloadToState(action.payload)
      };
    case ActionTypes.CANCEL_CONFIRMATION_ERROR:
      return {
        ...state,
        type: ErrorType.CANCEL_CONFIRMATION_ERROR,
        ...payloadToState(action.payload)
      };
    case ActionTypes.NOT_FOUND_PURCHASE_PLAN:
      return {
        ...state,
        type: ErrorType.NOT_FOUND_PURCHASE_PLAN,
        ...payloadToState(action.payload)
      };
    case ActionTypes.SYSTEM_ERROR_OCURRED:
      return {
        ...state,
        type: ErrorType.SYSTEM_ERROR,
        ...payloadToState(action.payload)
      };
    case ActionTypes.CLEAR:
      return initialState;
    default:
      return state;
  }
};
